/**
 * DetailsTab Component
 *
 * A form section component used for capturing and displaying core user details
 * (e.g., student information) within a tabbed interface. It integrates with
 * React Hook Form and composes multiple reusable form input components.
 *
 * ## Features:
 * - **Form Integration**: Works seamlessly with React Hook Form via the `control` prop.
 * - **Conditional Fields**: Displays the password field only when creating a new record.
 * - **Pre-filled Values**: Supports default values when editing existing records.
 * - **Attribute Selection**: Includes a multi-select for assigning attributes.
 * - **Email Aliases Management**: Allows adding and managing multiple email aliases.
 * - **External Identifiers**: Supports institution and integration IDs with contextual guidance.
 *
 * ## Props:
 * - `isEditAction` (boolean): Determines whether the form is in edit mode.
 *   - If `false`, the password field is displayed.
 * - `selectedRow` (any, optional): The existing data used to populate default values when editing.
 * - `control` (any): React Hook Form control object used to manage form state.
 *
 * ## Behavior:
 * - In **create mode** (`isEditAction = false`):
 *   - Password field is shown.
 * - In **edit mode** (`isEditAction = true`):
 *   - Password field is hidden.
 *   - Fields are pre-filled using `selectedRow` values.
 *
 * ## Example Usage:
 * ```tsx
 * <DetailsTab
 *   isEditAction={false}
 *   control={control}
 * />
 * ```
 *
 * @param {boolean} isEditAction - Indicates whether the form is in edit mode.
 * @param {any} [selectedRow] - Existing data for populating default values.
 * @param {any} control - React Hook Form control instance.
 */

import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme } from '@mui/material'
import React from 'react'
import FormInput from '../../../../components/FormElements/FormInput'
import AttributesMultiSelect from '../../../../components/FormElements/AttributesMultiSelect'
import EmailAliasesInput from '../../../../components/FormElements/EmailAliasesInput'
import FormSwitch from '../../../../components/FormElements/FormSwitch'
import { grey } from '../../../../colors'
import { ATTRIBUTE_TYPES } from '../../../../consts'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface DetailsTabProps {
  isEditAction: boolean
  selectedRow?: any
  control: any
  readonly?: boolean
}

export const DetailsTab: React.FC<DetailsTabProps> = ({
  isEditAction,
  selectedRow,
  control,
  readonly = false
}) => {
  const theme = useTheme()
  return (
    <Box display="flex" flexDirection="column" mt={2} gap={theme.spacing(6)}>
      <FormInput name="firstName" label="First name" placeholder="Enter first name" control={control} required />
      <FormInput name="lastName" label="Last name" placeholder="Enter last name" control={control} required />
      <FormInput name="studentId" label="School ID" placeholder="Enter a school ID" control={control} />
      <FormInput name="email" label="Email" placeholder="Enter an email" control={control} required />
      {
        !isEditAction &&
        <FormInput name="password" type="password" label="Password" placeholder="Enter a password" control={control} />
      }
      <AttributesMultiSelect
        extraParams={{ attributeType: ATTRIBUTE_TYPES.STUDENT_COURSE_ATTRIBUTES }}
        name="attributes"
        defaultAttributes={selectedRow?.attributes}
        skip={readonly}
      />
      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Additional Fields</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display='flex' flexDirection='column' gap={4}>
            <EmailAliasesInput name="aliases" defaultValue={selectedRow?.aliases} primaryEmail={selectedRow?.email} />
            <Box>
              <FormInput name="institutionId" defaultValue={selectedRow?.institutionId} label="Institution ID" control={control} placeholder='Institution ID' />
              <Typography fontSize="14px" mt={1} color={grey[850]}>
                Identifier from your SIS or data warehouse. Changing this may break syncing with your institutional data.
              </Typography>
            </Box>
            <Box>
              <FormInput name="integrationSourceId" label="Integration Source ID" defaultValue={selectedRow?.integrationSourceId} control={control} placeholder='Integration Source ID' />
              <Typography fontSize="14px" mt={1} color={grey[850]}>
                Identifier from an external system (e.g. LMS). Changing this may break the connection to that system and any related functionality.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      </Box>
  )
}
